<!--
  IconPickerDialog.vue — the "Change icon" dialog (go.lacak.io row ⋮ → Change icon).
  Mirrors the source: title "Icon", a "Use standard icon" toggle, a grid of vehicle icon
  templates, a "Use the group color" toggle, a colour palette, and Cancel/Save.
  On Save it emits { trackerId, icon, color } which the map applies to the tracker's marker.
  Reconstructed from the live change-icon dialog; internal impl re-authored.
-->
<template>
  <div class="ip-backdrop" @click.self="$emit('close')">
    <div class="ip-dialog">
      <div class="ip-head">Icon</div>
      <div class="ip-body">
        <div class="ip-label">Select an image:</div>
        <label class="ip-switch">
          <input type="checkbox" v-model="standard" /><span class="ip-track"><span class="ip-knob"></span></span> Use standard icon
        </label>

        <div v-if="standard" class="ip-grid">
          <button
            v-for="v in vehicles"
            :key="v.key"
            class="ip-cell"
            :class="{ sel: icon === v.key }"
            v-tip="v.label"
            @click="icon = v.key"
          ><i class="material-icons" :style="{ color: previewColor }">{{ v.icon }}</i></button>
        </div>
        <div v-else class="ip-custom">
          <i class="material-icons">image</i>
          <span>Custom image upload isn’t available in this demo.</span>
        </div>

        <div class="ip-label mt">Choose a color:</div>
        <label class="ip-switch">
          <input type="checkbox" v-model="groupColor" /><span class="ip-track"><span class="ip-knob"></span></span> Use the group color
        </label>
        <div class="ip-palette" :class="{ disabled: groupColor }">
          <button v-for="c in palette" :key="c" class="ip-swatch" :class="{ sel: color === c }" :style="{ background: c }" @click="pickColor(c)"></button>
        </div>
      </div>
      <div class="ip-actions">
        <button class="ip-cancel" @click="$emit('close')">CANCEL</button>
        <button class="ip-save" @click="save">SAVE</button>
      </div>
    </div>
  </div>
</template>

<script>
// The real "Choose a color" palette — 14 swatches (2 rows of 7), verified live from the
// deployed change-icon dialog (Material palette + the brand blue #1E96DC).
const PALETTE = [
  '#FF5722', '#FF9800', '#FFC107', '#FFEB3B', '#CDDC39', '#8BC34A', '#4CAF50',
  '#009688', '#00BCD4', '#1E96DC', '#3F51B5', '#673AB7', '#9C27B0', '#E91E63'
]
// Vehicle templates (Material Icons stand-ins for Navixy's top-down vehicle art).
const VEHICLES = [
  { key: 'moto', icon: 'two_wheeler', label: 'Motorcycle' },
  { key: 'car', icon: 'directions_car', label: 'Car' },
  { key: 'taxi', icon: 'local_taxi', label: 'Taxi' },
  { key: 'pickup', icon: 'airport_shuttle', label: 'Van' },
  { key: 'truck', icon: 'local_shipping', label: 'Truck' },
  { key: 'firetruck', icon: 'fire_truck', label: 'Fire truck' },
  { key: 'bus', icon: 'directions_bus', label: 'Bus' },
  { key: 'boat', icon: 'directions_boat', label: 'Boat' },
  { key: 'tractor', icon: 'agriculture', label: 'Tractor' },
  { key: 'bike', icon: 'pedal_bike', label: 'Bicycle' },
  { key: 'ev', icon: 'electric_car', label: 'Electric car' },
  { key: 'rv', icon: 'rv_hookup', label: 'RV' }
]

export default {
  name: 'IconPickerDialog',
  props: {
    tracker: { type: Object, required: true },
    // the tracker's current icon/colour override, so re-opening restores the prior choice
    override: { type: Object, default: null }
  },
  data() {
    const ov = this.override
    return {
      standard: ov ? !!ov.icon : true,
      groupColor: ov ? !ov.color : true,
      icon: (ov && ov.icon) || 'car',
      color: (ov && ov.color) || '#1E96DC',
      palette: PALETTE, vehicles: VEHICLES
    }
  },
  computed: {
    previewColor() { return this.groupColor ? '#17a2b8' : this.color }
  },
  methods: {
    pickColor(c) { this.groupColor = false; this.color = c },
    save() {
      this.$emit('save', {
        trackerId: this.tracker.id,
        icon: this.standard ? this.icon : null,
        color: this.groupColor ? null : this.color
      })
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.ip-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.4); display: flex; align-items: center; justify-content: center; z-index: 1200; }
.ip-dialog { width: 400px; max-width: 92vw; background: #fff; border-radius: 8px; box-shadow: 0 8px 30px rgba(0,0,0,.3); font-family: Roboto, sans-serif; display: flex; flex-direction: column; max-height: 88vh; }
.ip-head { font-size: 20px; font-weight: 500; color: #263238; padding: 18px 22px 12px; }
.ip-body { padding: 0 22px; overflow-y: auto; }
.ip-label { font-size: 14px; color: #607d8b; margin: 8px 0; }
.ip-label.mt { margin-top: 18px; }
.ip-switch { display: flex; align-items: center; gap: 10px; font-size: 14px; color: #37474f; cursor: pointer; margin: 6px 0 12px; }
.ip-switch input { display: none; }
.ip-track { width: 34px; height: 14px; border-radius: 7px; background: #b0bec5; position: relative; transition: background .15s; }
.ip-switch input:checked + .ip-track { background: #8ed0da; }
.ip-knob { position: absolute; top: -3px; left: -1px; width: 20px; height: 20px; border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,.4); transition: left .15s, background .15s; }
.ip-switch input:checked + .ip-track .ip-knob { left: 15px; background: #17a2b8; }
.ip-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin: 6px 0 4px; }
.ip-cell { border: 1px solid #e3e8ec; border-radius: 6px; height: 60px; background: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.ip-cell:hover { border-color: #b0d8e0; }
.ip-cell.sel { border-color: #17a2b8; box-shadow: 0 0 0 1px #17a2b8 inset; }
.ip-cell .material-icons { font-size: 30px; }
.ip-custom { display: flex; align-items: center; gap: 10px; padding: 20px 6px; color: #90a4ae; font-size: 13px; }
.ip-custom .material-icons { font-size: 28px; }
.ip-palette { display: grid; grid-template-columns: repeat(7, 1fr); gap: 8px; margin: 8px 0 4px; justify-items: center; }
.ip-palette.disabled { opacity: .45; pointer-events: none; }
.ip-swatch { width: 30px; height: 30px; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 0 0 1px #e0e0e0; cursor: pointer; }
.ip-swatch.sel { box-shadow: 0 0 0 2px #37474f; }
.ip-actions { display: flex; justify-content: flex-end; gap: 8px; padding: 14px 22px 18px; }
.ip-cancel, .ip-save { border: none; background: none; font-size: 14px; font-weight: 600; cursor: pointer; padding: 8px 12px; }
.ip-cancel { color: #607d8b; }
.ip-save { color: #17a2b8; }
</style>
